import { Injectable, DestroyRef, inject } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from "@angular/router";
import { filter } from "rxjs/operators";
import {
  HostNavigationChangeEvent,
  MicrofrontendNavigationRequestEvent,
  MicrofrontendHistoryRequestEvent,
  MicrofrontendNavigationRequestDetail,
  MicrofrontendHistoryRequestDetail
} from './routing-events.model';

/**
 * Service to manage routing and communication between the host application and microfrontends.
 *
 * This service listens to navigation events from the host application and broadcasts them to all microfrontends.
 * It also listens to navigation and history requests from microfrontends and handles them appropriately.
 *
 * @remarks
 * This service is initialized during application bootstrap via APP_INITIALIZER to ensure
 * event listeners are set up before the application starts routing.
 *
 * @see app.module.ts for APP_INITIALIZER configuration
 */
@Injectable({
  providedIn: "root",
})
export class CentralRouterService {
  private readonly destroyRef = inject(DestroyRef);
  constructor(private readonly router: Router) {}

  /**
   * Initializes the service by setting up all event listeners.
   *
   * This method is called during application bootstrap via APP_INITIALIZER.
   */
  load(): void {
    this.setupHostNavigationListener();
    this.setupMicrofrontendEventListeners();
  }

  /**
   * Sets up a listener for navigation changes in the host application.
   *
   * This listener captures navigation events and broadcasts the updated route to all microfrontends.
   */
  private setupHostNavigationListener(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe((event: NavigationEnd) => {
        this.handleHostNavigation(event.urlAfterRedirects);
      });
  }

  /**
   * Handles navigation events from the host application.
   *
   * @param url - The URL of the navigation event.
   *
   * This method cleans the URL (removing hash fragments) and broadcasts the navigation change to microfrontends.
   */
  private handleHostNavigation(url: string): void {
    const cleanUrl = url.replace("#", ""); // Remove hash if present
    this.broadcastNavigationChange(cleanUrl);
  }

  /**
   * Broadcasts a route change event to all microfrontends.
   *
   * @param hostRoute - The route to broadcast.
   *
   * This method creates a custom event with the route details and dispatches it globally.
   */
  private broadcastNavigationChange(hostRoute: string): void {
    const hostNavigationChangeEvent = new HostNavigationChangeEvent({
      route: hostRoute,
      timestamp: Date.now(),
    });
    globalThis.dispatchEvent(hostNavigationChangeEvent);
  }

  /**
   * Sets up listeners for events from microfrontends.
   *
   * This includes:
   * - Navigation requests from microfrontends.
   * - History manipulation requests (back, forward, go).
   */
  private setupMicrofrontendEventListeners(): void {
    // Listen for navigation requests from microfrontends
    globalThis.addEventListener(
      MicrofrontendNavigationRequestEvent.eventType,
      (rawEvent: Event) => {
        const navigationRequestEvent =
          rawEvent as CustomEvent<MicrofrontendNavigationRequestDetail>;
        this.handleMicrofrontendNavigationRequest(
          navigationRequestEvent.detail
        );
      }
    );

    // Listen for history requests from microfrontends
    globalThis.addEventListener(
      MicrofrontendHistoryRequestEvent.eventType,
      (rawEvent: Event) => {
        const historyRequestEvent =
          rawEvent as CustomEvent<MicrofrontendHistoryRequestDetail>;
        this.handleMicrofrontendHistoryRequest(historyRequestEvent.detail);
      }
    );
  }

  /**
   * Handles navigation requests from microfrontends.
   *
   * @param detail - The details of the navigation request, including the microfrontend and route.
   *
   * This method uses the Angular router to navigate to the requested route, triggering the host navigation listener.
   */
  private handleMicrofrontendNavigationRequest(
    detail: MicrofrontendNavigationRequestDetail
  ): void {
    const { route: microfrontendRoute } = detail;
    if (microfrontendRoute) {
      // Use Angular router to navigate (this will trigger our host navigation listener)
      this.router.navigateByUrl(microfrontendRoute);
    }
  }

  /**
   * Handles history manipulation requests from microfrontends.
   *
   * @param detail - The details of the history request, including the action (back, forward, go) and position.
   */
  private handleMicrofrontendHistoryRequest(
    detail: MicrofrontendHistoryRequestDetail
  ): void {
    const { action, position } = detail;

    switch (action) {
      case "back":
        globalThis.history.back();
        break;
      case "forward":
        globalThis.history.forward();
        break;
      case "go":
        if (typeof position === "number") {
          globalThis.history.go(position);
        }
        break;
    }
  }
}
